import React, { useEffect, useState } from "react";
import {
  Button,
  makeStyles,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from "@material-ui/core";
import { t } from "@configuratorware/configurator-frontendgui/Framework/i18n";
import CustomDialog from "@configuratorware/configurator-frontendgui/Shared/Components/CustomDialog";
import { SkiSchool } from "../../Types/Types";

export const useStyles = makeStyles(
  (theme) => ({
    skischoolDialog: {
      maxWidth: 450,
      margin: "auto",
      // styled required inorder to hide close button
      "& [class*=MuiDialogTitle] .material-icons": {
        display: "none",
      },
      "& [class*= MuiDialog-paper]": {
        overflowY: "visible",
      },
    },
    textField: {
      width: "100%",
      "& input": {
        padding: "10.5px 14px",
      },
    },
    schoolSelect: {
      width: "100%",
      margin: "24px 0px",
    },
    menuList: {
      backgroundColor: "#fff",
      position: "absolute",
      maxHeight: 200,
      width: "100%",
      zIndex: 2,
      overflowX: "hidden",
      overflowY: "auto",
      // adding similar boxShadow as select field menulist
      boxShadow:
        "0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)",
    },
    filterField: {
      position: "relative",
      marginBottom: 20,
    },
    menuItem: {
      display: "flex",
      padding: 10,
      marginTop: 5,
      cursor: "pointer",
    },
    activeButton: {
      width: "100%",
      padding: 8,
      textTransform: "none",
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      "&:hover": {
        backgroundColor: theme.palette.primary.main,
      },
    },
  }),
  { name: "SkiSchoolSelector" }
);

export interface SkiSchoolSelectorProps {
  fetchSkiSchools: () => void;
  selectSkiSchool: (SkiSchool) => void;
  selectedSkiSchool: SkiSchool;
  skiSchools: SkiSchool[];
  show: boolean;
  onClose: () => void;
}

const SkiSchoolSelector = (props) => {
  const classes = useStyles();
  const {
    fetchSkiSchools,
    selectSkiSchool,
    selectedSkiSchool,
    skiSchools,
    show,
    onClose,
  } = props;
  const [skischool, setSkiSchool] = useState<SkiSchool | null>(null);
  const [filteredSkiSchools, setFilteredSkiSchools] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [isTouched, setIsTouched] = useState(false);

  useEffect(() => {
    if (show) {
      fetchSkiSchools();
      setSkiSchool(selectedSkiSchool);
    }
  }, [show]);

  useEffect(() => {
    if (isTouched) {
      filterText
        ? filterSkiSchools(filterText)
        : setFilteredSkiSchools(skiSchools);
    }
  }, [isTouched]);

  const includesAll = (str: string, ...needles: string[]): boolean => {
    return [].concat(...needles).every((needle) => {
      return str.includes(needle);
    });
  };

  const filterSkiSchools = (value) => {
    setFilterText(value);

    if (value === "") {
      setFilteredSkiSchools(skiSchools);
      return;
    }
    const stringArr = value.toLowerCase().split(" ");
    const arr = [];

    skiSchools.forEach((ss) => {
      if (
        (ss.name && includesAll(ss.name.toLowerCase(), stringArr)) ||
        (ss.street && includesAll(ss.street.toLowerCase(), stringArr)) ||
        (ss.city && includesAll(ss.city.toLowerCase(), stringArr))
      ) {
        arr.push(ss);
      }
    });
    setFilteredSkiSchools(arr);
  };

  return (
    <CustomDialog
      open={show}
      className={classes.skischoolDialog}
      dialogTitle={t("skiSchool.title")}
    >
      <DialogContent style={{ overflow: "visible" }}>
        <div
          className={classes.filterField}
          onBlur={() => {
            setIsTouched(false);
            filteredSkiSchools.length > 0 && setFilteredSkiSchools([]);
          }}
        >
          <TextField
            value={skischool}
            className={classes.textField}
            variant="outlined"
            onFocus={() => setIsTouched(true)}
            onChange={(e) => filterSkiSchools(e.target.value)}
            placeholder={t("skiSchool.filterFor")}
          />
          {(filteredSkiSchools.length > 0 || filterText !== "") && (
            <div className={classes.menuList}>
              {filteredSkiSchools.map((school) => (
                <Typography
                  variant="body1"
                  className={classes.menuItem}
                  onMouseDown={(e) => {
                    setSkiSchool(school.name);
                    setFilteredSkiSchools([]);
                    setFilterText("");
                  }}
                > 
                {school.city != null ? (
                  school.city.length > 0 ?
                  school.name + ' (' + school.city + ')' : school.name
                ) : school.name}
                </Typography>
              ))}
              {filterText !== "" &&
                filteredSkiSchools.length === 0 &&
                isTouched && (
                  <Typography
                    variant="body1"
                    style={{ cursor: "text" }}
                    className={classes.menuItem}
                  >
                    {t("skiSchool.noEntries")}
                  </Typography>
                )}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            selectSkiSchool(skiSchools.find((ss) => ss.name === skischool));
            onClose();
          }}
          variant="contained"
          disabled={!skischool}
          classes={{ root: classes.activeButton }}
        >
          {t("skiSchool.ok")}
        </Button>
      </DialogActions>
    </CustomDialog>
  );
};

export default SkiSchoolSelector;
